/**
 * field-format-validation.ts — walidacja formatu wartości pola STEROWANA TYPEM pola
 * z katalogu (`SystemExtFieldType`).
 *
 * Jedno źródło reguł formatu dla WSZYSTKICH formularzy silnika (kartoteki + 7 klas
 * dokumentów, pola core i dodatkowe): zamiast wpinać walidację ręcznie per-formularz,
 * pytamy o typ pola i dobieramy walidator. Reguły dotyczą tylko typów z wrodzonym
 * formatem — `email`, `url`, `integer`, `numeric`, `date`, `timestamp`. Pozostałe
 * typy (`varchar`, `text`, `boolean`, `select`, `multiselect`, `reference`, `file`)
 * nie mają reguły formatu → `null`.
 *
 * Kontrakt:
 *  - Zwraca STABILNY kod błędu (sufiks klucza `common.validationIssue.<kod>`), nie
 *    gotowy tekst — tłumaczenie robi callsite przez `t()` (i18n, zero surowych
 *    stringów w UI, patrz CLAUDE.md §4.5). `null` = wartość poprawna dla tego typu.
 *  - PUSTĄ wartość (null/undefined/''/[]/) uznaje za poprawną — obecność pola
 *    („wymagane") to OSOBNA reguła (patrz `common.validationIssue.required`), nie
 *    format. Dzięki temu ten sam walidator działa dla pól opcjonalnych i wymaganych.
 *
 * NIP/PESEL NIE są typami pól (to pola core `varchar` ze specjalną obsługą) — ich
 * walidator sumy kontrolnej dokłada się per-pole osobno, nie przez tę oś typów.
 */

export type FieldFormatErrorCode =
  | 'invalidEmail'
  | 'invalidUrl'
  | 'invalidInteger'
  | 'invalidNumber'
  | 'invalidDate'
  | 'invalidDateTime';

/** Czy wartość jest „pusta" — presence to osobna reguła, tu ją przepuszczamy. */
export function isEmptyFieldValue(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

// E-mail: pragmatyczny wzorzec (jedno @, część lokalna i domena bez spacji, kropka
// w domenie). Świadomie NIE pełny RFC 5322 — to walidacja UX na froncie, ostatnią
// linią obrony jest serwer (Zod `.email()`).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidNumericString(raw: string): boolean {
  // Akceptuj kropkę i przecinek jako separator dziesiętny (PL) + opcjonalny znak.
  const normalized = raw.trim().replace(',', '.');
  if (normalized === '' || normalized === '-' || normalized === '+') return false;
  return /^[+-]?\d*\.?\d+$/.test(normalized);
}

function isValidIsoDate(raw: string): boolean {
  // YYYY-MM-DD + realna data (odrzuca 2026-02-31).
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw.trim());
  if (!m) return false;
  const [, y, mo, d] = m;
  const dt = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
  return (
    dt.getUTCFullYear() === Number(y) &&
    dt.getUTCMonth() === Number(mo) - 1 &&
    dt.getUTCDate() === Number(d)
  );
}

/**
 * Waliduje format wartości względem typu pola z katalogu. Zwraca kod błędu do
 * przetłumaczenia (`common.validationIssue.<kod>`) albo `null` gdy poprawna/pusta.
 */
export function validateFieldFormat(
  fieldType: string,
  value: unknown,
): FieldFormatErrorCode | null {
  if (isEmptyFieldValue(value)) return null;

  switch (fieldType) {
    case 'email':
      return typeof value === 'string' && EMAIL_RE.test(value.trim()) ? null : 'invalidEmail';

    case 'url':
      try {
        const u = new URL(String(value).trim());
        // Pole „url" oczekuje adresu WWW — odrzucamy inne schematy (tel:, mailto:,
        // javascript:, foo:bar), które `new URL` przepuszcza jako poprawne URI.
        return u.protocol === 'http:' || u.protocol === 'https:' ? null : 'invalidUrl';
      } catch {
        return 'invalidUrl';
      }

    case 'integer': {
      const raw = String(value).trim().replace(',', '.');
      return /^[+-]?\d+$/.test(raw) ? null : 'invalidInteger';
    }

    case 'numeric':
      return isValidNumericString(String(value)) ? null : 'invalidNumber';

    case 'date':
      return isValidIsoDate(String(value)) ? null : 'invalidDate';

    case 'timestamp': {
      // ISO 8601 datetime (spójnie ze ścisłym `date`): YYYY-MM-DD[T/spacja]HH:mm[:ss][.sss][strefa].
      // Samo `new Date` jest zbyt pobłażliwe (łyka „2026/9/8", „Sep 8 2026" — zależnie od
      // silnika), więc najpierw wymuszamy kształt regexem, potem realność daty.
      const raw = String(value).trim();
      const ISO_DT = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/;
      if (!ISO_DT.test(raw)) return 'invalidDateTime';
      return Number.isNaN(new Date(raw).getTime()) ? 'invalidDateTime' : null;
    }

    default:
      // varchar / text / boolean / select / multiselect / reference / file —
      // brak wrodzonej reguły formatu.
      return null;
  }
}

/** Pojedyncze sprawdzenie: klucz błędu (dla `useFieldErrors`), typ pola i wartość. */
export interface FieldFormatCheck {
  /** Klucz, pod którym błąd trafi do `useFieldErrors` (core: stateKey, ext: slug). */
  key: string;
  fieldType: string;
  value: unknown;
}

/**
 * Uruchamia walidację formatu dla listy pól i zwraca mapę `klucz → kod błędu` dla
 * tych, które NIE przeszły. Silnik-agnostyczny — callsite buduje listę `checks`
 * (np. `buildEngineFormatChecks` z configu silnika). Puste/poprawne pola pomijane.
 */
export function collectFieldFormatErrors(
  checks: FieldFormatCheck[],
): Record<string, FieldFormatErrorCode> {
  const out: Record<string, FieldFormatErrorCode> = {};
  for (const { key, fieldType, value } of checks) {
    const code = validateFieldFormat(fieldType, value);
    if (code) out[key] = code;
  }
  return out;
}
